import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { UploadFileService } from './upload.file.service';
import { isArray, map } from 'lodash';
import { DialogService } from '../dialog/dialog.service';

@Component({
  selector: 'upload-file',
  templateUrl: './upload.file.template.html'
})
export class UploadFileComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() uploadDataSource: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() uploadAlternateDataSource: any;
  @Input() showInput: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() csvRecordsEvent: EventEmitter<any> = new EventEmitter();
  @ViewChild('fileImportInput', { static: true })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileImportInput: any;
  isHeaderPresent: boolean;
  headerData: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any;
  preDefinedHeader: string[];
  preDefinedAlternateHeader: string[];
  selectedPreDefinedHeader: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvFileData: any;

  constructor(
    private readonly papa: Papa,
    private readonly fileUtil: UploadFileService,
    private readonly dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.preDefinedHeader = map(this.uploadDataSource.default, 'name');
    // used for Apex support
    this.preDefinedAlternateHeader = map(this.uploadAlternateDataSource.default, 'name');
    this.papaParseCSV();
  }

  ngOnDestroy() {
    this.fileReset();
  }

  papaParseCSV(): void {
    this.options = {
      skipEmptyLines: true,
      dynamicTyping: true,
      delimiter: ',',
      complete: results => {
        if (results && isArray(results.data)) {
          this.columnData = results.data;
          this.headerData = this.columnData[0];

          // ATW-5244 - sometime there will extra columns available which need to be removed and
          // check only valid header which required for uploading
          if (
            this.headerData.length !== this.preDefinedHeader.length ||
            this.headerData.length !== this.preDefinedAlternateHeader.length
          ) {
            const headerExists = this.checkAllHeaderExists(
              this.headerData,
              this.preDefinedAlternateHeader
            );
            if (headerExists) {
              this.columnData = this.populateExactHeaderData(
                this.headerData,
                this.preDefinedAlternateHeader,
                this.columnData
              );
            } else {
              const headerExists = this.checkAllHeaderExists(
                this.headerData,
                this.preDefinedHeader
              );
              if (headerExists) {
                this.columnData = this.populateExactHeaderData(
                  this.headerData,
                  this.preDefinedHeader,
                  this.columnData
                );
              }
            }
            this.headerData = this.columnData[0];
          }

          // We are supporting two csv files one with 10 columns and other with 6 columns for apex support
          // Based on csv header column length we will determine to use preDefinedAlternateHeader or preDefinedHeader
          this.selectedPreDefinedHeader =
            this.headerData?.length === 6 ? this.preDefinedAlternateHeader : this.preDefinedHeader;

          this.isHeaderPresent = this.fileUtil.validateHeaders(
            this.headerData,
            this.selectedPreDefinedHeader
          );

          if (this.isHeaderPresent) {
            this.columnData = this.columnData.slice(1);
          } else {
            // Header column is not valid. Show error to user.
            const errorMessage = this.fileUtil.getHeaderErrorMessage(
              this.headerData,
              this.selectedPreDefinedHeader
            );
            this.showDialogMessage(errorMessage);
            return;
          }

          const message = this.fileUtil.validateDataRecordsArrayFromCSVFile(
            this.uploadDataSource,
            this.uploadAlternateDataSource,
            this.columnData,
            this.headerData,
            this.isHeaderPresent
          );
          if (message) {
            this.showDialogMessage(message);
          } else {
            const parsedDataObj = {
              isHeaderPresent: this.isHeaderPresent,
              csvData: this.parseCSVFormData(),
              jsonData: this.populateCSVData(this.headerData, this.columnData)
            };
            this.csvRecordsEvent.emit(parsedDataObj);
          }
        } else {
          this.showDialogMessage('CSV file is not valid');
        }
        this.fileReset();
      },
      error(err) {
        this.showDialogMessage(err);
      }
    };
  }

  checkAllHeaderExists(headerData: string[], definedHeader: string[]): boolean {
    for (const headerName of definedHeader) {
      if (headerData.indexOf(headerName) === -1) {
        return false;
      }
    }
    return true;
  }

  populateExactHeaderData(
    headerData: string[],
    definedHeader: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnData: Array<any[]>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Array<any[]> {
    const rowArrayItems = [];
    columnData.forEach(rowItems => {
      const columnArrayItems = [];
      definedHeader.forEach(headerItem => {
        const columnIndex = headerData.indexOf(headerItem);
        if (columnIndex !== -1) {
          columnArrayItems.push(rowItems[columnIndex]);
        }
      });
      rowArrayItems.push(columnArrayItems);
    });
    return rowArrayItems;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  showDialogMessage(message: any): boolean {
    this.dialogService.showInfo('information', 'body', message, 'CSV Parse Error');
    this.fileReset();
    return false;
  }

  parseCSVFormData(): FormData {
    const formData = new FormData();
    formData.append('file', this.csvFileData);
    return formData;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  populateCSVData(header: string[], column: string[]): any[] {
    const dataArray = [];
    column.forEach(columnData => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = {};
      header.forEach((headerData, headerIndex) => {
        result[headerData] = columnData[headerIndex];
      });
      dataArray.push(result);
    });
    return dataArray;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  fileChangeListener($event: any): void {
    const target = $event.target || $event.srcElement;
    const files = target.files;
    if (!this.fileUtil.isCSVFile(files[0])) {
      this.showDialogMessage('Please import valid CSV file');
    }
    this.csvFileData = files[0];
    this.papa.parse(this.csvFileData, this.options);
  }

  fileReset(): void {
    this.fileImportInput.nativeElement.value = '';
  }
}
